import type { AgricobotAsset } from '@/types/AgricobotAsset';
import { AssetTypes } from '@/types/AssetTypes';
import { TimeSync } from '@/utils/timesync/timesync';
import { GenericAsset, type InboundRecord } from '../../../types/GenericAsset';
import {
  formatBooleanStatus,
  formatDecimal,
  formatPercentage,
  formatRpm,
  formatSmartValue,
  formatVoltage,
} from '../../../utils/formatUtils';
import { AgricobotCompanionServices } from '../services/AgricobotCompanionServices';

// Enum for message types specific to AgricobotAsset
export enum AgricobotCompanionMessageTypes {
  HEARTBEAT = 'companion_heartbeat',
  TELEMETRY_DATA = 'companion_telemetry',
}

export const getAllAgricobotCompanionMessageTypes = () => {
  return Object.values(AgricobotCompanionMessageTypes);
};

export type AgricobotCompanionHeartbeatData = {
  frame_id: string | null;
  robot_id: string | null;
  companion_id: string | null;
  companion_model: string | null;
  companion_status: number | null;
  companion_sftw_version: string | null;
};

// TODO: implement this
export type AgricobotCompanionTelemetryData = {
  frame_id: string | null;
  robot_id: string | null;
  timestamp: Date | null;
  pos_x_ecef: number | null;
  pos_y_ecef: number | null;
  pos_z_ecef: number | null;
  vel_x_ms: number | null;
  rot_x: number | null;
  rot_y: number | null;
  rot_z: number | null;
  rot_w: number | null;
  solution_type: number | null;
  vision_status: boolean | null;
  anti_collision_status: number | null;
  identified_obstacle_type: string | null;
  engine_status: boolean | null;
  engine_rpm: number | null;
  fuel_level_percent: number | null;
  ecu_voltage_v: number | null;
  water_tank_level_percent: number | null;
  attachment_type: string | null;
  attachment_status: boolean | null;
  sprayer_left_status: boolean | null;
  sprayer_right_status: boolean | null;
  pump_pressure_bar: number | null;
  flow_rate_l_min: number | null;
  plan_name: string | null;
  plan_status: number | null;
  plan_completion_percent: number | null;
  anomaly_id: number | null;
  anomaly_type: string | null;
  data_quality: DataQuality | null;
};

export type DataQuality = {
  is_valid: boolean | null;
  array_lengths_mismatch: boolean | null;
  expected_length: number | null;
  actual_lengths: Record<string, number> | null;
  validation_warnings: string[] | null;
  processing_strategy: string | null;
};

interface ICompanionAsset {
  masterAsset: AgricobotAsset;

  companionId: string | null;

  heartbeatData: AgricobotCompanionHeartbeatData;
  telemetryData: AgricobotCompanionTelemetryData;

  lastHeartbeat: Date | null;
  lastTelemetry: Date | null;
}

export class AgricobotCompanionAsset extends GenericAsset implements ICompanionAsset {
  protected supportedMessageTypes: string[] = [
    AgricobotCompanionMessageTypes.HEARTBEAT,
    AgricobotCompanionMessageTypes.TELEMETRY_DATA,
  ];

  public masterAsset: AgricobotAsset;

  public companionId: string | null;

  public heartbeatData: AgricobotCompanionHeartbeatData;
  public telemetryData: AgricobotCompanionTelemetryData;

  // Timestamps
  public lastHeartbeat: Date | null;
  public lastTelemetry: Date | null;

  constructor(InboundRecords: InboundRecord[], masterAsset: AgricobotAsset) {
    // Sort records by timestamp in descending order to process the newest first
    InboundRecords.sort(
      (a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime(),
    );

    super(InboundRecords);

    this.masterAsset = masterAsset;

    if (InboundRecords.length > 0) {
      this.lastMessageReceived = new Date(InboundRecords[0].received_at);
    }

    /* ================================================
     * INITIALIZATION PART
     * ================================================ */

    this.companionId = null;

    // Initialize timestamps
    this.lastHeartbeat = null;
    this.lastTelemetry = null;

    // Initialize heartbeatData with default null values
    this.heartbeatData = {
      frame_id: null,
      robot_id: null,
      companion_id: null,
      companion_model: null,
      companion_status: null,
      companion_sftw_version: null,
    };

    // Initialize telemetryData with default null values
    this.telemetryData = {
      frame_id: null,
      robot_id: null,
      timestamp: null,
      pos_x_ecef: null,
      pos_y_ecef: null,
      pos_z_ecef: null,
      vel_x_ms: null,
      rot_x: null,
      rot_y: null,
      rot_z: null,
      rot_w: null,
      solution_type: null,
      vision_status: null,
      anti_collision_status: null,
      identified_obstacle_type: null,
      engine_status: null,
      engine_rpm: null,
      fuel_level_percent: null,
      ecu_voltage_v: null,
      water_tank_level_percent: null,
      attachment_type: null,
      attachment_status: null,
      sprayer_left_status: null,
      sprayer_right_status: null,
      pump_pressure_bar: null,
      flow_rate_l_min: null,
      plan_name: null,
      plan_status: null,
      plan_completion_percent: null,
      anomaly_id: null,
      anomaly_type: null,
      data_quality: null,
    };

    /* ================================================
     * ASSIGNATION PART
     * ================================================ */

    let latestTelemetryRecord: InboundRecord | undefined;
    let latestCameraRecord: InboundRecord | undefined;
    let latestHeartbeatRecord: InboundRecord | undefined;

    for (const record of InboundRecords) {
      if (
        record.message_type === AgricobotCompanionMessageTypes.HEARTBEAT &&
        !latestHeartbeatRecord
      ) {
        latestHeartbeatRecord = record;
      } else if (
        record.message_type === AgricobotCompanionMessageTypes.TELEMETRY_DATA &&
        !latestTelemetryRecord
      ) {
        latestTelemetryRecord = record;
      }

      // If we have found the latest record for all supported types, we can stop
      if (latestTelemetryRecord && latestCameraRecord && latestHeartbeatRecord) {
        break;
      }
    }

    if (latestHeartbeatRecord) {
      const heartbeat = latestHeartbeatRecord.payload as AgricobotCompanionHeartbeatData;
      this.companionId = heartbeat.companion_id;
      this.heartbeatData.frame_id = heartbeat.frame_id;
      this.heartbeatData.robot_id = heartbeat.robot_id;
      this.heartbeatData.companion_id = heartbeat.companion_id;
      this.heartbeatData.companion_model = heartbeat.companion_model;
      this.heartbeatData.companion_status = heartbeat.companion_status;
      this.heartbeatData.companion_sftw_version = heartbeat.companion_sftw_version;
      this.device_name = heartbeat.companion_model;
      this.lastHeartbeat = new Date(latestHeartbeatRecord.received_at);
    }

    if (latestTelemetryRecord) {
      const tel = this.telemetryData;
      const payloadObject = latestTelemetryRecord.payload as {
        frame_id?: string;
        timestamp?: number;
        pos_x_ecef?: number;
        pos_y_ecef?: number;
        pos_z_ecef?: number;
        vel_x_ms?: number;
        rot_x?: number;
        rot_y?: number;
        rot_z?: number;
        rot_w?: number;
        solution_type?: number;
        vision_status?: boolean;
        anti_collision_status?: number;
        identified_obstacle_type?: string;
        engine_status?: boolean;
        engine_rpm?: number;
        fuel_level_percent?: number;
        ecu_voltage_v?: number;
        water_tank_level_percent?: number;
        attachment_type?: string;
        attachment_status?: boolean;
        sprayer_left_status?: boolean;
        sprayer_right_status?: boolean;
        pump_pressure_bar?: number;
        flow_rate_l_min?: number;
        plan_name?: string;
        plan_status?: number;
        plan_completion_percent?: number;
        anomaly_id?: number;
        anomaly_type?: string;
        data_quality?: DataQuality;
        TIMESTAMP?: {
          FrameId?: string;
          Package?: number;
          Seq?: number;
          Stamp?: string;
        };
      };

      tel.frame_id = payloadObject.frame_id ?? tel.frame_id;
      tel.pos_x_ecef = payloadObject.pos_x_ecef ?? tel.pos_x_ecef;
      tel.pos_y_ecef = payloadObject.pos_y_ecef ?? tel.pos_y_ecef;
      tel.pos_z_ecef = payloadObject.pos_z_ecef ?? tel.pos_z_ecef;
      tel.vel_x_ms = payloadObject.vel_x_ms ?? tel.vel_x_ms;
      tel.rot_x = payloadObject.rot_x ?? tel.rot_x;
      tel.rot_y = payloadObject.rot_y ?? tel.rot_y;
      tel.rot_z = payloadObject.rot_z ?? tel.rot_z;
      tel.rot_w = payloadObject.rot_w ?? tel.rot_w;
      tel.solution_type = payloadObject.solution_type ?? tel.solution_type;
      tel.vision_status = payloadObject.vision_status ?? tel.vision_status;
      tel.anti_collision_status = payloadObject.anti_collision_status ?? tel.anti_collision_status;
      tel.identified_obstacle_type =
        payloadObject.identified_obstacle_type ?? tel.identified_obstacle_type;
      tel.engine_status = payloadObject.engine_status ?? tel.engine_status;
      tel.engine_rpm = payloadObject.engine_rpm ?? tel.engine_rpm;
      tel.fuel_level_percent = payloadObject.fuel_level_percent ?? tel.fuel_level_percent;
      tel.ecu_voltage_v = payloadObject.ecu_voltage_v ?? tel.ecu_voltage_v;
      tel.water_tank_level_percent =
        payloadObject.water_tank_level_percent ?? tel.water_tank_level_percent;
      tel.attachment_type = payloadObject.attachment_type ?? tel.attachment_type;
      tel.attachment_status = payloadObject.attachment_status ?? tel.attachment_status;
      tel.sprayer_left_status = payloadObject.sprayer_left_status ?? tel.sprayer_left_status;
      tel.sprayer_right_status = payloadObject.sprayer_right_status ?? tel.sprayer_right_status;
      tel.pump_pressure_bar = payloadObject.pump_pressure_bar ?? tel.pump_pressure_bar;
      tel.flow_rate_l_min = payloadObject.flow_rate_l_min ?? tel.flow_rate_l_min;
      tel.plan_name = payloadObject.plan_name ?? tel.plan_name;
      tel.plan_status = payloadObject.plan_status ?? tel.plan_status;
      tel.plan_completion_percent =
        payloadObject.plan_completion_percent ?? tel.plan_completion_percent;
      tel.anomaly_id = payloadObject.anomaly_id ?? tel.anomaly_id;
      tel.anomaly_type = payloadObject.anomaly_type ?? tel.anomaly_type;
      tel.data_quality = payloadObject.data_quality ?? tel.data_quality;

      if (payloadObject.TIMESTAMP?.Stamp !== undefined) {
        tel.timestamp = new Date(payloadObject.TIMESTAMP.Stamp);
      } else if (payloadObject.timestamp !== undefined) {
        tel.timestamp = new Date(payloadObject.timestamp);
      }
      if (payloadObject.TIMESTAMP?.FrameId !== undefined) {
        tel.frame_id = payloadObject.TIMESTAMP.FrameId;
      } else if (payloadObject.frame_id !== undefined) {
        tel.frame_id = payloadObject.frame_id;
      }

      this.lastTelemetry = new Date(latestTelemetryRecord.received_at);
    }

    if (latestHeartbeatRecord) {
      this.lastHeartbeat = new Date(latestHeartbeatRecord.received_at);
    }

    // Set the fixed image path for companion assets
    this.assetImagePath = '/agricobots/companions/drone_companion.png';
  }

  public isOnline(): boolean {
    const timeoutMs = new Date(
      TimeSync.now().getTime() -
        import.meta.env.VITE_AGRICOBOTASSET_COMPANION_ONLINE_TIMEOUT_MINUTES * 60 * 1000,
    );
    return !!(
      (this.lastHeartbeat && this.lastHeartbeat > timeoutMs) ||
      (this.lastTelemetry && this.lastTelemetry > timeoutMs)
    );
  }

  public getStatus(): unknown {
    return this.heartbeatData.companion_status;
  }

  public getAssetType(): string {
    return AssetTypes.AGRICOBOT_COMPANION;
  }

  public static getMessageTypes(): string[] {
    return Object.values(AgricobotCompanionMessageTypes);
  }

  public updateInboundRecord(records: InboundRecord[]): void {
    // Sort records by timestamp in descending order to process the newest first
    // This ensures that the asset's state reflects the most recent data
    records.sort((a, b) => new Date(b.received_at).getTime() - new Date(a.received_at).getTime());

    if (records.length > 0) {
      this.lastMessageReceived = new Date(records[0].received_at);
    }

    let latestHeartbeatProcessed = false;
    let latestTelemetryProcessed = false;

    for (const record of records) {
      let payload = record.payload as Record<string, unknown>; // Cast once for easier access

      const companionService = new AgricobotCompanionServices();
      if (companionService.isInboundRecordAnArray(record)) {
        const singleRecord = companionService.inboundRecordArrayToSingle(
          this.companionId as string,
          record,
          record,
        );
        if (!singleRecord) {
          throw new Error(
            `Failed to convert array-based record to single-companion record for companion ${this.companionId}`,
          );
        }
        payload = singleRecord.payload as Record<string, unknown>;
      }

      switch (record.message_type) {
        case AgricobotCompanionMessageTypes.HEARTBEAT:
          if (!latestHeartbeatProcessed) {
            latestHeartbeatProcessed = true;
            const heartbeat = payload as AgricobotCompanionHeartbeatData;
            this.companionId = heartbeat.companion_id;
            this.heartbeatData.frame_id = heartbeat.frame_id;
            this.heartbeatData.robot_id = heartbeat.robot_id;
            this.heartbeatData.companion_id = heartbeat.companion_id;
            this.heartbeatData.companion_model = heartbeat.companion_model;
            this.heartbeatData.companion_status = heartbeat.companion_status;
            this.heartbeatData.companion_sftw_version = heartbeat.companion_sftw_version;
            this.lastHeartbeat = new Date(record.received_at);
          }
          break;
        case AgricobotCompanionMessageTypes.TELEMETRY_DATA:
          if (!latestTelemetryProcessed) {
            const tel = this.telemetryData; // Shorthand for AgricobotTelemetry
            const payloadObject = payload as {
              frame_id?: string;
              timestamp?: number; // Assuming timestamp can come as a number if not a full ROS Header
              pos_x_ecef?: number;
              pos_y_ecef?: number;
              pos_z_ecef?: number;
              vel_x_ms?: number;
              rot_x?: number;
              rot_y?: number;
              rot_z?: number;
              rot_w?: number;
              solution_type?: number;
              vision_status?: boolean;
              anti_collision_status?: number;
              identified_obstacle_type?: string;
              engine_status?: boolean;
              engine_rpm?: number;
              fuel_level_percent?: number;
              ecu_voltage_v?: number;
              water_tank_level_percent?: number;
              attachment_type?: string;
              attachment_status?: boolean;
              sprayer_left_status?: boolean;
              sprayer_right_status?: boolean;
              pump_pressure_bar?: number;
              flow_rate_l_min?: number;
              plan_name?: string;
              plan_status?: number;
              plan_completion_percent?: number;
              anomaly_id?: number;
              anomaly_type?: string;
              data_quality?: DataQuality;
              TIMESTAMP?: {
                // Assuming TIMESTAMP can be part of the payload, as seen in updateFromDatastore
                FrameId?: string;
                Package?: number;
                Seq?: number;
                Stamp?: string;
              };
            };

            tel.frame_id = payloadObject.frame_id ?? tel.frame_id;
            tel.pos_x_ecef = payloadObject.pos_x_ecef ?? tel.pos_x_ecef;
            tel.pos_y_ecef = payloadObject.pos_y_ecef ?? tel.pos_y_ecef;
            tel.pos_z_ecef = payloadObject.pos_z_ecef ?? tel.pos_z_ecef;
            tel.vel_x_ms = payloadObject.vel_x_ms ?? tel.vel_x_ms;
            tel.rot_x = payloadObject.rot_x ?? tel.rot_x;
            tel.rot_y = payloadObject.rot_y ?? tel.rot_y;
            tel.rot_z = payloadObject.rot_z ?? tel.rot_z;
            tel.rot_w = payloadObject.rot_w ?? tel.rot_w;
            tel.solution_type = payloadObject.solution_type ?? tel.solution_type;
            tel.vision_status = payloadObject.vision_status ?? tel.vision_status;
            tel.anti_collision_status =
              payloadObject.anti_collision_status ?? tel.anti_collision_status;
            tel.identified_obstacle_type =
              payloadObject.identified_obstacle_type ?? tel.identified_obstacle_type;
            tel.engine_status = payloadObject.engine_status ?? tel.engine_status;
            tel.engine_rpm = payloadObject.engine_rpm ?? tel.engine_rpm;
            tel.fuel_level_percent = payloadObject.fuel_level_percent ?? tel.fuel_level_percent;
            tel.ecu_voltage_v = payloadObject.ecu_voltage_v ?? tel.ecu_voltage_v;
            tel.water_tank_level_percent =
              payloadObject.water_tank_level_percent ?? tel.water_tank_level_percent;
            tel.attachment_type = payloadObject.attachment_type ?? tel.attachment_type;
            tel.attachment_status = payloadObject.attachment_status ?? tel.attachment_status;
            tel.sprayer_left_status = payloadObject.sprayer_left_status ?? tel.sprayer_left_status;
            tel.sprayer_right_status =
              payloadObject.sprayer_right_status ?? tel.sprayer_right_status;
            tel.pump_pressure_bar = payloadObject.pump_pressure_bar ?? tel.pump_pressure_bar;
            tel.flow_rate_l_min = payloadObject.flow_rate_l_min ?? tel.flow_rate_l_min;
            tel.plan_name = payloadObject.plan_name ?? tel.plan_name;
            tel.plan_status = payloadObject.plan_status ?? tel.plan_status;
            tel.plan_completion_percent =
              payloadObject.plan_completion_percent ?? tel.plan_completion_percent;
            tel.anomaly_id = payloadObject.anomaly_id ?? tel.anomaly_id;
            tel.anomaly_type = payloadObject.anomaly_type ?? tel.anomaly_type;
            tel.data_quality = payloadObject.data_quality ?? tel.data_quality;

            // Handle device timestamp from ROS TelemetryData Header (std_msgs/Header)
            // payload.TIMESTAMP will have .Stamp as a string
            if (payloadObject.TIMESTAMP?.Stamp !== undefined) {
              tel.timestamp = new Date(payloadObject.TIMESTAMP.Stamp);
            } else if (payloadObject.timestamp !== undefined) {
              // Fallback to direct timestamp if Header is not present
              tel.timestamp = new Date(payloadObject.timestamp);
            }
            // Update localization FrameID from TelemetryData Header if available
            if (payloadObject.TIMESTAMP?.FrameId !== undefined) {
              tel.frame_id = payloadObject.TIMESTAMP.FrameId; // Update frame_id in telemetryData
            } else if (payloadObject.frame_id !== undefined) {
              tel.frame_id = payloadObject.frame_id; // Fallback to direct frame_id
            }

            this.lastTelemetry = new Date(record.received_at); // Update lastTelemetry when telemetry data is processed
            latestTelemetryProcessed = true; // Mark as processed to only get the latest
          }
          break;
        default:
          console.warn(
            '[AgricobotCompanionAsset] updateInboundRecord: message type not recognized:',
            record.message_type,
          );
          console.warn('[AgricobotCompanionAsset] updateInboundRecord: payload:', payload);
          break;
      }

      // Once both latest telemetry and camera data are processed, we can stop
      if (latestHeartbeatProcessed && latestTelemetryProcessed) {
        break;
      }
    }
  }

  /* ===============================
   * POSITION, ORIENTATION AND VELOCITY RELATED METHODS
  =============================== */

  /**
   * Converts ECEF coordinates to WGS84 geographic coordinates
   * Uses the same iterative algorithm as BaseAsset.getCoordinates.
   * @returns [longitude, latitude] in decimal degrees, or null if position is not available
   */
  public getGeographicCoordinates(invertLngLat = false): [number, number] | null {
    if (
      this.telemetryData.pos_x_ecef == null ||
      this.telemetryData.pos_y_ecef == null ||
      this.telemetryData.pos_z_ecef == null
    ) {
      return null;
    }

    const { x, y, z } = {
      x: this.telemetryData.pos_x_ecef,
      y: this.telemetryData.pos_y_ecef,
      z: this.telemetryData.pos_z_ecef,
    };

    const a = 6378137.0; // semi-major axis in meters
    const e2 = 0.006694379990141316; // first eccentricity squared

    // Distance from Earth's center to point projected onto XY plane
    const p = Math.sqrt(x * x + y * y);

    // Initial value for latitude
    let lat = Math.atan2(z, p * (1 - e2));

    let prevLat: number;
    // Iterative algorithm to calculate latitude (usually converges in 2-3 iterations)
    do {
      prevLat = lat;
      const sinLat = Math.sin(lat);
      const N = a / Math.sqrt(1 - e2 * sinLat * sinLat);
      lat = Math.atan2(z + e2 * N * sinLat, p);
    } while (Math.abs(lat - prevLat) > 1e-12);

    // Calculate longitude
    const lon = Math.atan2(y, x);

    // Convert to degrees
    const lonDeg = lon * (180 / Math.PI);
    const latDeg = lat * (180 / Math.PI);

    if (invertLngLat) {
      return [latDeg, lonDeg];
    }
    return [lonDeg, latDeg];
  }

  /* ===============================
   * EXPORT, TRANSLATION AND VALUE-FORMATTING METHODS
  =============================== */

  public exportGenericProperties(): Record<string, unknown> {
    return {
      asset_id: this.asset_id,
      node_id: this.node_id,
      device_id: this.device_id,
      device_name: this.device_name,
      companion_id: this.companionId,
      status: this.getStatus(),
      lastMessageReceived: this.lastMessageReceived,
      lastMessageType: this.lastMessageType,
      isOnline: this.isOnline(),
    };
  }

  /**
   * Exports all asset properties
   * @returns Object containing all asset properties with original property names
   */
  public exportAllProperties(): Record<string, unknown> {
    return {
      ...this.exportGenericProperties(),
      ...this.exportTelemetryProperties(),
    };
  }

  /**
   * Exports heartbeat data with the original field names as they appear in the payload
   * @returns Object containing all heartbeat properties with original payload field names
   */
  public exportHeartbeatProperties(): Record<string, unknown> {
    return {
      frame_id: this.heartbeatData.frame_id,
      robot_id: this.heartbeatData.robot_id,
      companion_id: this.heartbeatData.companion_id,
      companion_model: this.heartbeatData.companion_model,
      companion_status: this.heartbeatData.companion_status,
      companion_sftw_version: this.heartbeatData.companion_sftw_version,
    };
  }

  /**
   * Exports all telemetry data with the original field names as they appear in the payload
   * @returns Object containing all telemetry properties with original payload field names
   */
  public exportTelemetryProperties(): Record<string, unknown> {
    // Create a base object with all telemetry fields with their original names
    const telemetryData: Record<string, unknown> = {
      // ECEF Position
      pos_x_ecef: this.telemetryData.pos_x_ecef,
      pos_y_ecef: this.telemetryData.pos_y_ecef,
      pos_z_ecef: this.telemetryData.pos_z_ecef,

      // Orientation
      rot_x: this.telemetryData.rot_x,
      rot_y: this.telemetryData.rot_y,
      rot_z: this.telemetryData.rot_z,
      rot_w: this.telemetryData.rot_w,

      // Velocity
      vel_x_ms: this.telemetryData.vel_x_ms,

      // Solution & Vision Status
      solution_type: this.telemetryData.solution_type,
      vision_status: this.telemetryData.vision_status,

      // Collision & Obstacle
      anti_collision_status: this.telemetryData.anti_collision_status,
      identified_obstacle_type: this.telemetryData.identified_obstacle_type,

      // Engine
      engine_status: this.telemetryData.engine_status,
      engine_rpm: this.telemetryData.engine_rpm,
      fuel_level_percent: this.telemetryData.fuel_level_percent,
      ecu_voltage_v: this.telemetryData.ecu_voltage_v,

      // Other
      water_tank_level_percent: this.telemetryData.water_tank_level_percent,
      attachment_status: this.telemetryData.attachment_status,
      attachment_type: this.telemetryData.attachment_type,

      // Anomaly
      anomaly_id: this.telemetryData.anomaly_id,
      anomaly_type: this.telemetryData.anomaly_type,

      // Sprayer System
      sprayer_left_status: this.telemetryData.sprayer_left_status,
      sprayer_right_status: this.telemetryData.sprayer_right_status,
      pump_pressure_bar: this.telemetryData.pump_pressure_bar,
      flow_rate_l_min: this.telemetryData.flow_rate_l_min,

      // Mission Plan
      plan_name: this.telemetryData.plan_name,
      plan_status: this.telemetryData.plan_status,
      plan_completion_percent: this.telemetryData.plan_completion_percent,
    };

    // Add data_quality to the exported properties
    if (this.telemetryData.data_quality) {
      telemetryData.data_quality = this.telemetryData.data_quality;
    }

    return telemetryData;
  }

  /**
   * Translates export keys from exportTelemetryProperties and exportGenericProperties to localized strings
   * @param key The export key to translate
   * @param t Translation function from useI18n
   * @returns The localized string for the key
   */
  public translateExportKey(
    key: string,
    t?: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    // If no translation function provided, return the key as-is
    if (!t) {
      return key;
    }

    // Handle all export keys from both exportTelemetryProperties and exportGenericProperties
    switch (key) {
      // Identifiers
      case 'node_id':
        return t('asset.agricobots_robot.exportkey.node_id');
      case 'asset_id':
        return t('asset.agricobots_robot.exportkey.asset_id');
      case 'device_id':
        return t('asset.agricobots_robot.exportkey.device_id');
      case 'device_name':
        return t('asset.agricobots_robot.exportkey.device_name');

      // Generic/logic properties
      case 'lastMessageType':
        return t('asset.agricobots_robot.exportkey.last_message_type');
      case 'status':
        return t('asset.agricobots_robot.exportkey.status');
      case 'lastMessageReceived':
        return t('asset.agricobots_robot.exportkey.last_message_received');

      // Heartbeat properties
      case 'frame_id':
        return t('asset.agricobots_companion.exportkey.frame_id');
      case 'robot_id':
        return t('asset.agricobots_companion.exportkey.robot_id');
      case 'companion_id':
        return t('asset.agricobots_companion.exportkey.companion_id');
      case 'companion_model':
        return t('asset.agricobots_companion.exportkey.companion_model');
      case 'companion_status':
        return t('asset.agricobots_companion.exportkey.companion_status');
      case 'companion_sftw_version':
        return t('asset.agricobots_companion.exportkey.companion_sftw_version');
      case 'companion_count':
        return t('asset.agricobots_companion.exportkey.companion_count');

      // TelemetryData properties
      // ECEF Position
      case 'pos_x_ecef':
        return t('asset.agricobots_robot.exportkey.pos_x_ecef');
      case 'pos_y_ecef':
        return t('asset.agricobots_robot.exportkey.pos_y_ecef');
      case 'pos_z_ecef':
        return t('asset.agricobots_robot.exportkey.pos_z_ecef');

      // Orientation
      case 'rot_x':
        return t('asset.agricobots_robot.exportkey.rot_x');
      case 'rot_y':
        return t('asset.agricobots_robot.exportkey.rot_y');
      case 'rot_z':
        return t('asset.agricobots_robot.exportkey.rot_z');
      case 'rot_w':
        return t('asset.agricobots_robot.exportkey.rot_w');

      // Velocity
      case 'vel_x_ms':
        return t('asset.agricobots_robot.exportkey.vel_x_ms');

      // Solution & Vision Status
      case 'solution_type':
        return t('asset.agricobots_robot.exportkey.solution_type');
      case 'vision_status':
        return t('asset.agricobots_robot.exportkey.vision_status');

      // Collision & Obstacle
      case 'anti_collision_status':
        return t('asset.agricobots_robot.exportkey.anti_collision_status');
      case 'identified_obstacle_type':
        return t('asset.agricobots_robot.exportkey.identified_obstacle_type');

      // Engine
      case 'engine_status':
        return t('asset.agricobots_robot.exportkey.engine_status');
      case 'engine_rpm':
        return t('asset.agricobots_robot.exportkey.engine_rpm');
      case 'fuel_level_percent':
        return t('asset.agricobots_robot.exportkey.fuel_level_percent');
      case 'ecu_voltage_v':
        return t('asset.agricobots_robot.exportkey.ecu_voltage_v');

      // Other
      case 'water_tank_level_percent':
        return t('asset.agricobots_robot.exportkey.water_tank_level_percent');
      case 'attachment_status':
        return t('asset.agricobots_robot.exportkey.attachment_status');
      case 'attachment_type':
        return t('asset.agricobots_robot.exportkey.attachment_type');

      // Anomaly
      case 'anomaly_id':
        return t('asset.agricobots_robot.exportkey.anomaly_id');
      case 'anomaly_type':
        return t('asset.agricobots_robot.exportkey.anomaly_type');

      // Sprayer System
      case 'sprayer_left_status':
        return t('asset.agricobots_robot.exportkey.sprayer_left_status');
      case 'sprayer_right_status':
        return t('asset.agricobots_robot.exportkey.sprayer_right_status');
      case 'pump_pressure_bar':
        return t('asset.agricobots_robot.exportkey.pump_pressure_bar');
      case 'flow_rate_l_min':
        return t('asset.agricobots_robot.exportkey.flow_rate_l_min');

      // Mission Plan
      case 'plan_name':
        return t('asset.agricobots_robot.exportkey.plan_name');
      case 'plan_status':
        return t('asset.agricobots_robot.exportkey.plan_status');
      case 'plan_completion_percent':
        return t('asset.agricobots_robot.exportkey.plan_completion_percent');

      case 'data_quality':
        return t('asset.agricobots_robot.exportkey.data_quality');

      // Default fallback
      default:
        console.error('key not recognized in translateExportKey, default fallback for key:', key);
        return t(`asset.agricobots_robot.exportkey.${key}`) || key;
    }
  }

  /**
   * Formats export values from exportTelemetryProperties and exportGenericProperties
   * @param key The export key
   * @param value The value to format
   * @param t Translation function from useI18n (optional)
   * @returns Formatted value as string
   */
  public formatExportValue(
    key: string,
    value: unknown,
    t?: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    // Default values for translations
    const notAvailable = t ? t('common.not_available') : 'N/A';
    const none = t ? t('common.none') : 'None';

    // Return notAvailable for null or undefined values
    if (value === null || value === undefined) {
      return notAvailable;
    }

    // Format values based on key
    switch (key) {
      // Identifiers
      case 'node_id':
      case 'asset_id':
      case 'device_id':
      case 'device_name':
        return String(value);

      // Generic/logic properties
      case 'lastMessageType':
        return String(value);
      case 'status':
        return this._formatRbtStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );
      case 'lastMessageReceived':
        return this.lastMessageReceived?.toUTCString() ?? notAvailable;

      // Heartbeat properties
      case 'frame_id':
      case 'robot_id':
      case 'companion_count':
        return String(value);
      case 'companion_id':
      case 'companion_model':
      case 'companion_sftw_version':
      case 'companion_status':
        return Array.isArray(value) ? value.join(', ') : String(value);

      // TelemetryData properties
      // ECEF Position
      case 'pos_x_ecef':
      case 'pos_y_ecef':
      case 'pos_z_ecef':
        return formatDecimal(value as number, 3);

      // Orientation
      case 'rot_x':
      case 'rot_y':
      case 'rot_z':
      case 'rot_w':
        return formatDecimal(value as number, 4);

      // Velocity
      case 'vel_x_ms':
        return `${formatDecimal(value as number, 2)} m/s`;

      // Solution & Vision Status
      case 'solution_type':
        return this._formatRbtSolutionType(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );

      case 'vision_status':
        return this._formatRbtVisionStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );

      // Collision & Obstacle
      case 'anti_collision_status':
        return String(value);

      case 'identified_obstacle_type':
        return String(value) || none;

      // Engine
      case 'engine_status':
        return this._formatRbtEngineStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );

      case 'engine_rpm':
        return formatRpm(value as number);

      case 'fuel_level_percent':
        return formatPercentage(value as number, 1);

      case 'ecu_voltage_v':
        return formatVoltage(value as number);

      // Other
      case 'water_tank_level_percent':
        return formatPercentage(value as number, 1);

      case 'attachment_status':
        return this._formatRbtAttachmentStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );
      case 'attachment_type':
        return String(value) || none;

      // Anomaly
      case 'anomaly_id':
        return String(value);

      case 'anomaly_type':
        return String(value) || none;

      // Sprayer System
      case 'sprayer_left_status':
      case 'sprayer_right_status':
        return this._formatRbtSprayerLeftStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );

      case 'pump_pressure_bar':
        return `${formatDecimal(value as number, 1)} bar`;
      case 'flow_rate_l_min':
        return `${formatDecimal(value as number, 1)} L/min`;

      // Mission Plan
      case 'plan_name':
        return String(value) || none;

      case 'plan_status':
        return this._formatRbtPlanStatus(
          value,
          t as (key: string, params?: Record<string, unknown>) => string,
        );

      case 'plan_completion_percent':
        return formatPercentage(value as number, 1);

      case 'data_quality':
        return value ? JSON.stringify(value, null, 2) : notAvailable;

      // Default fallback
      default:
        console.error(
          'key not recognized in formatExportValue, default fallback for key:',
          key,
          'value:',
          value,
        );
        return formatSmartValue(value);
    }
  }

  public isCalculatedProperty(key: string): boolean {
    switch (key) {
      case 'status':
        return true;
      default:
        return false;
    }
  }

  _formatRbtStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    const integerValue = Number.parseInt(value as string);
    switch (integerValue) {
      case 0:
        return t('asset.agricobots_robot.mappings.status.0');
      case 1:
        return t('asset.agricobots_robot.mappings.status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSolutionType(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
        return t('asset.agricobots_robot.mappings.solution_type.0');
      case 1:
        return t('asset.agricobots_robot.mappings.solution_type.1');
      case 2:
        return t('asset.agricobots_robot.mappings.solution_type.2');
      case 3:
        return t('asset.agricobots_robot.mappings.solution_type.3');
      case 4:
        return t('asset.agricobots_robot.mappings.solution_type.4');
      case 5:
        return t('asset.agricobots_robot.mappings.solution_type.5');
      case 6:
        return t('asset.agricobots_robot.mappings.solution_type.6');
      case 7:
        return t('asset.agricobots_robot.mappings.solution_type.7');
      case 8:
        return t('asset.agricobots_robot.mappings.solution_type.8');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtVisionStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.vision_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.vision_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtAntiCollisionStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
        return t('asset.agricobots_robot.mappings.anti_collision.0');
      case 1:
        return t('asset.agricobots_robot.mappings.anti_collision.1');
      case 2:
        return t('asset.agricobots_robot.mappings.anti_collision.2');
      case 3:
        return t('asset.agricobots_robot.mappings.anti_collision.3');
      case 4:
        return t('asset.agricobots_robot.mappings.anti_collision.4');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtEngineStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.engine_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.engine_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtAttachmentStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.attachment_status.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.attachment_status.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSprayerLeftStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.sprayer_left.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.sprayer_left.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtSprayerRightStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    switch (value) {
      case 0:
      case false:
        return t('asset.agricobots_robot.mappings.sprayer_left.0');
      case 1:
      case true:
        return t('asset.agricobots_robot.mappings.sprayer_left.1');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  _formatRbtPlanStatus(
    value: unknown,
    t: (key: string, params?: Record<string, unknown>) => string,
  ): string {
    const integerValue = Number.parseInt(value as string);
    switch (integerValue) {
      case 0:
        return t('asset.agricobots_robot.mappings.plan_status.0');
      case 1:
        return t('asset.agricobots_robot.mappings.plan_status.1');
      case 2:
        return t('asset.agricobots_robot.mappings.plan_status.2');
      case 3:
        return t('asset.agricobots_robot.mappings.plan_status.3');
      case 4:
        return t('asset.agricobots_robot.mappings.plan_status.4');
      default:
        return t('asset.agricobots_robot.mappings.unrecognized_value', {
          value: String(value),
        });
    }
  }

  /* ===============================
   * UI HELPER METHODS
  =============================== */

  public isPropertyChartable(key: string): boolean {
    switch (key) {
      case 'vel_x_ms':
      case 'solution_type':
      case 'vision_status':
      case 'engine_rpm':
      case 'fuel_level_percent':
      case 'ecu_voltage_v':
      case 'water_tank_level_percent':
      case 'attachment_status':
      case 'sprayer_left_status':
      case 'sprayer_right_status':
      case 'pump_pressure_bar':
      case 'flow_rate_l_min':
        return true;
      default:
        return false;
    }
  }
}
